import { create } from 'zustand';
import { Adventure, PetAssets, PetCard } from "@/constant/schema";
import { StageId } from "@/constant/adventureStages";
import supabase from "@/lib/supabase";
import useAuthStore from './useAuthStore';
import { getTodayFinishedLessonCount } from '@/constant/tasks';
import { apiClient } from '@/lib/api-client';
import { calculateOutfitDiff, insertOutfitChangeMessage, fetchAssetDetails } from "@/lib/outfit-logger";
import { CustomizedMiniPet, CanvasAnimationData } from '@/app/magic-lab/types';
import { getSelectedCustomizedMinipet, selectCustomizedMinipet, getAllCustomizedMinipets } from '@/app/magic-lab/services/customized_minipet_service';

// 工具函数：从 CustomizedMiniPet 构建 CanvasAnimationData（纯函数，无网络请求）
const buildCanvasDataFromMinipet = (minipet: CustomizedMiniPet): CanvasAnimationData => {
  return {
    sessionId: minipet.session_id,
    frames: minipet.frame_urls,
    bundleUrl: minipet.bundle_url || null,  // 新格式：优先使用 bundle
    fps: 24,
    width: 512,
    height: 512,
    totalFrames: minipet.frame_urls.length,
    audioUrl: minipet.audio_url || null,
    hasAudio: minipet.has_audio,
  };
};

interface PetStore {
  // 设置pengi的背景图片
  pengiBg: PetAssets | null;
  setPengiBg: (bg: PetAssets | null) => void;
  // 设置pengi的装扮
  pengiFit: { [key: string]: PetAssets | null };
  setPengiFit: (pengiFit: { [key: string]: PetAssets | null }) => void;

  // ============ Minipet 状态 (单一数据源) ============
  // Regular minipet
  minipet: PetAssets | null;
  hasMinipets: number[];

  // Customized minipet + canvasData (从 frame_urls 构建)
  customizedMinipet: CustomizedMiniPet | null;
  canvasData: CanvasAnimationData | null;
  hasCustomMinipets: number[];

  // 用户所有自定义 minipets (资源列表)
  customizedMinipets: CustomizedMiniPet[];

  // 预览状态 (PetMini 模态框用)
  previewMinipet: PetAssets | null;
  previewCustomizedMinipet: CustomizedMiniPet | null;
  previewCanvasData: CanvasAnimationData | null;

  // ============ Minipet Actions ============
  setMinipet: (minipet: PetAssets | null, userId: string) => Promise<void>;
  loadCustomizedMinipets: (userId: string) => Promise<void>;
  setPreviewMinipet: (type: 'regular' | 'customized', minipet: PetAssets | CustomizedMiniPet | null) => void;
  confirmSelection: (userId: string) => Promise<void>;
  clearSelection: (userId: string) => Promise<void>;
  resetPreview: () => void;
  // 设置pengi的背景弹窗是否打开
  isBgOpen: boolean;
  setIsBgOpen: (isBgOpen: boolean) => void;
  // 设置pengi的装扮弹窗是否打开
  isCabinetOpen: boolean;
  setIsCabinetOpen: (isCabinetOpen: boolean) => void;
  // 设置pengi的对话框是否打开
  showDialog: boolean;
  setShowDialog: (showDialog: boolean) => void;
  // 设置是否收到礼物
  receiveGift: boolean;
  setReceiveGift: (receiveGift: boolean) => void;
  // 是否发送过第一条消息
  hasSentFirstMessage: boolean;
  // 礼物出现后是否发送了消息
  hasMessagedAfterGift: boolean;
  setHasMessagedAfterGift: (hasMessagedAfterGift: boolean, userId: string) => Promise<void>;
  // 设置pengi的商店弹窗是否打开
  isShopOpen: boolean;
  setIsShopOpen: (isShopOpen: boolean) => void;
  isMiniPetOpen: boolean;
  setIsMiniPetOpen: (isMiniPetOpen: boolean) => void;
  cabinet: { [key: string]: number[] };
  setCabinet: (cabinet: { [key: string]: number[] }) => void;
  isCardOpen: boolean;
  setIsCardOpen: (isCardOpen: boolean) => void;
  cardRefreshTrigger: number;
  triggerCardRefresh: () => void;
  activeCardSeries: string | null;
  setActiveCardSeries: (series: string | null) => void;
  isVoiceOpen: boolean;
  setIsVoiceOpen: (isVoiceOpen: boolean) => void;
  isMapOpen: boolean;
  setIsMapOpen: (isMapOpen: boolean) => void;
  currentStage: StageId;
  setCurrentStage: (stage: StageId, userId: string) => Promise<void>;

  addToCabinet: (pengiFit: { [key: string]: PetAssets | null }, userId: string) => Promise<void>;
  saveOutfit: (pengiFit: { [key: string]: PetAssets | null }, userId: string) => Promise<void>;
  // 初始化方法
  initPet: (userId: string) => Promise<void>;
  buyMinipet: (minipetId: number, userId: string) => Promise<void>;
  buyCustomMinipet: (customMinipetId: number, userId: string) => Promise<void>;

  currentAdventure: Adventure | null;
  adventureReward: PetCard | null;

  // Countdown state for adventure
  remainingSeconds: number;
  loadingAdventure: boolean;
  isCollectingReward: boolean;
  setRemainingSeconds: (seconds: number) => void;
  setIsCollectingReward: (isCollecting: boolean) => void;
  startCountdown: (adventureId: number) => void;
  stopCountdown: () => void;

  updateAdventureStatus: (adventure_id: number, status: string, return_time?: string) => Promise<void>;
  fetchAdventure: (userid: string) => Promise<void>;
}

// 通用函数：更新 pet_onboard_data 的单个字段
const updatePetOnboardField = async (userId: string, fieldName: string, fieldValue: any) => {
  try {
    const profile = useAuthStore.getState().profile;
    const petData = profile?.pet_onboard_data || {};
    const updatedPetData = {
      ...petData,
      [fieldName]: fieldValue
    };

    await useAuthStore.getState().updateProfile({ pet_onboard_data: updatedPetData });
  } catch (error) {
    console.error(`Error updating pet_onboard_data.${fieldName}:`, error);
  }
};

const usePetStore = create<PetStore>((set, get) => ({
  // 初始状态
  pengiBg: null,
  pengiFit: {
    face: null,
    head: null,
    top: null,
    hand: null,
    ground: null,
    back: null,
    down: null,
    suitecase: null
  },
  hasMinipets: [],
  hasCustomMinipets: [],
  minipet: null,
  customizedMinipet: null,
  canvasData: null,
  customizedMinipets: [],
  previewMinipet: null,
  previewCustomizedMinipet: null,
  previewCanvasData: null,
  isBgOpen: false,
  isCabinetOpen: false,
  isShopOpen: false,
  isMiniPetOpen: false,
  isCardOpen: false,
  cardRefreshTrigger: 0,
  activeCardSeries: null,
  isVoiceOpen: false,
  isMapOpen: false,
  showDialog: false,
  receiveGift: true,
  hasSentFirstMessage: false,
  hasMessagedAfterGift: false,
  cabinet: {
    face: [],
    head: [],
    top: [],
    hand: [],
    ground: [],
    back: [],
    down: [],
    suitecase: []
  },

  currentAdventure: null,
  adventureReward: null,

  // Countdown state
  remainingSeconds: 0,
  loadingAdventure: false,
  isCollectingReward: false,

  // Stage state
  currentStage: 'stage1',

  // 设置方法
  setPengiBg: (bg) => set({ pengiBg: bg }),
  setPengiFit: (pengiFit) => set({ pengiFit }),
  setIsBgOpen: (isBgOpen) => set({ isBgOpen }),
  setIsCabinetOpen: (isCabinetOpen) => set({ isCabinetOpen }),
  setIsShopOpen: (isShopOpen) => set({ isShopOpen }),
  setShowDialog: (showDialog) => set({ showDialog }),
  setIsCardOpen: (isCardOpen) => set({ isCardOpen }),
  setActiveCardSeries: (series) => set({ activeCardSeries: series }),
  setIsMapOpen: (isMapOpen) => set({ isMapOpen }),
  setReceiveGift: (receiveGift) => set({ receiveGift }),
  setCabinet: (cabinet) => set({ cabinet }),
  setIsVoiceOpen: (isVoiceOpen) => set({ isVoiceOpen }),
  triggerCardRefresh: () => set((state) => ({ cardRefreshTrigger: state.cardRefreshTrigger + 1 })),
  setHasMessagedAfterGift: async (hasMessagedAfterGift: boolean, userId: string) => {
    // 立即更新本地状态
    set({ hasMessagedAfterGift });
    // 异步更新数据库（不阻塞）
    updatePetOnboardField(userId, 'has_messaged_after_gift', hasMessagedAfterGift);
  },
  setIsMiniPetOpen: (isMiniPetOpen) => set({ isMiniPetOpen }),
  setCurrentStage: async (stage: StageId, userId: string) => {
    // 立即更新本地状态
    set({ currentStage: stage });
    // 异步更新数据库（不阻塞）
    try {
      await useAuthStore.getState().updateProfile({ current_stage: stage });
    } catch (error) {
      console.error('Error updating current_stage:', error);
    }
  },
  setMinipet: async (minipet: PetAssets | null, userId: string) => {
    set({ minipet });
    try {
      await useAuthStore.getState().updateProfile({ current_minipet: minipet?.id || null });
    } catch (error) {
      console.error('Error updating minipet:', error);
    }
  },

  // ============ New Minipet Actions ============
  loadCustomizedMinipets: async (userId: string) => {
    try {
      const minipets = await getAllCustomizedMinipets(userId);
      set({ customizedMinipets: minipets });
      console.log('✅ Loaded customized minipets:', minipets.length);
    } catch (error) {
      console.error('❌ Error loading customized minipets:', error);
    }
  },

  setPreviewMinipet: (type: 'regular' | 'customized', minipet: PetAssets | CustomizedMiniPet | null) => {
    if (type === 'regular') {
      set({
        previewMinipet: minipet as PetAssets | null,
        previewCustomizedMinipet: null,
        previewCanvasData: null,
      });
    } else {
      const customized = minipet as CustomizedMiniPet | null;
      const canvasData = customized ? buildCanvasDataFromMinipet(customized) : null;
      console.log('[setPreviewMinipet] Building canvasData:', {
        minipetId: customized?.id,
        frameUrlsCount: customized?.frame_urls?.length,
        builtCanvasData: canvasData ? {
          sessionId: canvasData.sessionId,
          framesCount: canvasData.frames?.length,
          firstFrame: canvasData.frames?.[0],
        } : null,
      });
      set({
        previewMinipet: null,
        previewCustomizedMinipet: customized,
        previewCanvasData: canvasData,
      });
    }
  },

  confirmSelection: async (userId: string) => {
    const { previewMinipet, previewCustomizedMinipet, previewCanvasData } = get();

    console.log('[confirmSelection] Preview state:', {
      previewMinipet: previewMinipet?.id,
      previewCustomizedMinipet: previewCustomizedMinipet?.id,
      previewCanvasData: previewCanvasData ? {
        sessionId: previewCanvasData.sessionId,
        framesCount: previewCanvasData.frames?.length,
      } : null,
    });

    try {
      if (previewMinipet) {
        // 选择 regular minipet，清除 customized 选择
        await Promise.all([
          useAuthStore.getState().updateProfile({ current_minipet: previewMinipet.id }),
          selectCustomizedMinipet(userId, null),  // 清除 customized 选择（单独的数据库字段）
        ]);
        set({
          minipet: previewMinipet,
          customizedMinipet: null,
          canvasData: null,
        });
        console.log('✅ Regular minipet selected:', previewMinipet.id);
      } else if (previewCustomizedMinipet) {
        // 选择 customized minipet，清除 regular 选择
        await Promise.all([
          useAuthStore.getState().updateProfile({ current_minipet: null }),
          selectCustomizedMinipet(userId, previewCustomizedMinipet.id),
        ]);
        set({
          minipet: null,
          customizedMinipet: previewCustomizedMinipet,
          canvasData: previewCanvasData,
        });
        console.log('✅ Customized minipet selected:', previewCustomizedMinipet.id);
      }

      // 清除预览状态
      set({
        previewMinipet: null,
        previewCustomizedMinipet: null,
        previewCanvasData: null,
      });
    } catch (error) {
      console.error('❌ Error confirming selection:', error);
      throw error;
    }
  },

  clearSelection: async (userId: string) => {
    try {
      // 清除数据库中的所有选择（并行执行）
      await Promise.all([
        useAuthStore.getState().updateProfile({ current_minipet: null }),
        selectCustomizedMinipet(userId, null),
      ]);

      set({
        minipet: null,
        customizedMinipet: null,
        canvasData: null,
        previewMinipet: null,
        previewCustomizedMinipet: null,
        previewCanvasData: null,
      });
      console.log('✅ Minipet selection cleared');
    } catch (error) {
      console.error('❌ Error clearing selection:', error);
      throw error;
    }
  },

  resetPreview: () => {
    const { minipet, customizedMinipet, canvasData } = get();
    set({
      previewMinipet: minipet,
      previewCustomizedMinipet: customizedMinipet,
      previewCanvasData: canvasData,
    });
  },

  buyMinipet: async (minipet_id: number, userId: string) => {
    const { hasMinipets, hasCustomMinipets } = get();
    // 判断minipet_id是否为数字，也有可能是"196"这种字符串
    if (isNaN(Number(minipet_id))) {
      return;
    }
    const minipetId = Number(minipet_id);
    if (hasMinipets.includes(minipetId)) {
      return;
    }

    set({ hasMinipets: [...hasMinipets, minipetId] });
    try {
      await useAuthStore.getState().updateProfile({
        minipets: {
          minipet: [...hasMinipets, minipetId],
          custom: hasCustomMinipets
        }
      });
    } catch (error) {
      console.error('Error buying minipet:', error);
    }
  },

  buyCustomMinipet: async (customMinipetId: number, userId: string) => {
    const { hasCustomMinipets, hasMinipets } = get();
    if (hasCustomMinipets.includes(customMinipetId)) {
      return;
    }

    set({ hasCustomMinipets: [...hasCustomMinipets, customMinipetId] });
    try {
      await useAuthStore.getState().updateProfile({
        minipets: {
          minipet: hasMinipets,
          custom: [...hasCustomMinipets, customMinipetId]
        }
      });
    } catch (error) {
      console.error('Error buying custom minipet:', error);
    }
  },

  // 保存装扮到数据库
  saveOutfit: async (pengiFit: { [key: string]: PetAssets | null }, userId: string) => {
    // 将pengiFit转换为json字符串，格式为{"face": "1", "head": "2", "top": "3", "hand": "4", "ground": "5", "back": "6"}
    const currentOutfit = Object.fromEntries(Object.entries(pengiFit).map(([key, value]) => [key, value?.id]));

    try {
      // Get old outfit for comparison
      const profile = await useAuthStore.getState().getProfile(userId);
      const oldOutfit = profile?.current_outfit || {};

      // Fetch asset details for all assets in old and new outfit
      const allAssetIds = [
        ...Object.values(oldOutfit).filter(Boolean) as number[],
        ...Object.values(currentOutfit).filter(Boolean) as number[]
      ];

      // Parallelize: fetch asset details and save outfit simultaneously
      const [assetDetails] = await Promise.all([
        fetchAssetDetails(allAssetIds),
        useAuthStore.getState().updateProfile({ current_outfit: currentOutfit })
      ]);

      // Calculate and record outfit change (fire-and-forget, non-blocking)
      const changes = calculateOutfitDiff(oldOutfit as Record<string, number>, currentOutfit as Record<string, number>, assetDetails);
      if (changes.putOn.length > 0 || changes.replacements.length > 0 || changes.removed.length > 0) {
        // Don't await - let it run in background
        insertOutfitChangeMessage(userId, changes).catch(err =>
          console.error('Error recording outfit change:', err)
        );
      }
    } catch (error) {
      console.error('Error saving outfit:', error);
    }
  },

  // 添加到衣柜
  addToCabinet: async (pengiFit: { [key: string]: PetAssets | null }, userId: string) => {
    const { cabinet } = get();

    // 如果pengiFit中部位是有值的，则添加到newCabinet
    const newCabinet: { [key: string]: number[] } = { ...cabinet };
    if (pengiFit.face) {
      newCabinet.face = [...cabinet.face, pengiFit.face.id];
    }
    if (pengiFit.head) {
      newCabinet.head = [...cabinet.head, pengiFit.head.id];
    }
    if (pengiFit.top) {
      newCabinet.top = [...cabinet.top, pengiFit.top.id];
    }
    if (pengiFit.hand) {
      newCabinet.hand = [...cabinet.hand, pengiFit.hand.id];
    }
    if (pengiFit.ground) {
      newCabinet.ground = [...cabinet.ground, pengiFit.ground.id];
    }
    if (pengiFit.back) {
      newCabinet.back = [...cabinet.back, pengiFit.back.id];
    }
    if (pengiFit.down) {
      newCabinet.down = [...cabinet.down, pengiFit.down.id];
    }
    if (pengiFit.suitecase) {
      newCabinet.suitecase = [...cabinet.suitecase, pengiFit.suitecase.id];
    }

    console.log('newCabinet', newCabinet);

    try {
      await useAuthStore.getState().updateProfile({ cabinet: newCabinet });
      set({ cabinet: newCabinet });
    } catch (error) {
      console.error('Error adding to cabinet:', error);
    }
  },

  // 初始化宠物数据
  initPet: async (userId: string) => {
    // 强制从数据库获取最新 profile，确保获取到后台的最新修改
    const profile = await useAuthStore.getState().getProfile(userId);

    if (!profile) {
      console.error('Profile not found');
      return;
    }

    // 并行加载 customized minipet
    const customizedMinipetPromise = getSelectedCustomizedMinipet(userId).catch(err => {
      console.error('Error loading customized minipet:', err);
      return null;
    });

    const data = profile;
    // Batch all state updates to reduce re-renders
    const stateUpdates: Partial<PetStore> = {};

    if (data) {
      const petOnboardData = data?.pet_onboard_data || {};
      stateUpdates.receiveGift = data?.receive_gems;
      stateUpdates.hasSentFirstMessage = petOnboardData?.has_sent_first_message || false;
      stateUpdates.hasMessagedAfterGift = petOnboardData?.has_messaged_after_gift || false;

      const currentOutfit = data?.current_outfit;
      const currentBg = data?.current_bg;
      const currentMinipet = data?.current_minipet;

      if (currentOutfit || currentBg || currentMinipet) {
        // 收集所有需要查询的ID
        const idsToFetch = currentOutfit ? Object.values(currentOutfit).filter(Boolean) : [];
        if (currentBg) {
          idsToFetch.push(currentBg);
        }
        if (currentMinipet) {
          idsToFetch.push(currentMinipet);
        }

        if (idsToFetch.length > 0) {
          // 通过id从pet_assets表中获取
          const { data: petAssets, error: petAssetsError } = await supabase
            .from('pet_assets')
            .select('*')
            .in('id', idsToFetch);

          if (petAssetsError) {
            console.error('Error fetching pet assets:', petAssetsError);
          }

          if (petAssets) {
            const petAssetsMap = new Map(petAssets.map(asset => [asset.part, asset]));

            // 设置装扮
            if (currentOutfit) {
              stateUpdates.pengiFit = {
                face: petAssetsMap.get('face') || null,
                head: petAssetsMap.get('head') || null,
                top: petAssetsMap.get('top') || null,
                hand: petAssetsMap.get('hand') || null,
                ground: petAssetsMap.get('ground') || null,
                back: petAssetsMap.get('back') || null,
                down: petAssetsMap.get('down') || null,
                suitecase: petAssetsMap.get('suitecase') || null
              };
            }

            // 设置背景
            if (currentBg) {
              stateUpdates.pengiBg = petAssetsMap.get('background') || petAssetsMap.get('adventureBg') || null;
            }
            if (currentMinipet) {
              stateUpdates.minipet = petAssetsMap.get('minipet') || null;
            }
          }
        }
      }

      // 设置衣柜
      const userCabinet = data?.cabinet;
      if (userCabinet) {
        stateUpdates.cabinet = {
          face: userCabinet.face || [],
          head: userCabinet.head || [],
          top: userCabinet.top || [],
          hand: userCabinet.hand || [],
          ground: userCabinet.ground || [],
          back: userCabinet.back || [],
          down: userCabinet.down || [],
          suitecase: userCabinet.suitecase || []
        };
      }

      // 设置已拥有的minipet
      const hasMinipets = data?.minipets;
      if (hasMinipets) {
        stateUpdates.hasMinipets = hasMinipets.minipet || [];
        stateUpdates.hasCustomMinipets = hasMinipets.custom || [];
      }

      // 设置当前stage
      stateUpdates.currentStage = data?.current_stage || 'stage1';
    }

    // 等待 customized minipet 加载完成，并构建 canvasData
    const customizedMinipet = await customizedMinipetPromise;
    const canvasData = customizedMinipet ? buildCanvasDataFromMinipet(customizedMinipet) : null;
    stateUpdates.customizedMinipet = customizedMinipet;
    stateUpdates.canvasData = canvasData;

    // Apply all state updates in a single batch
    set(stateUpdates as any);
    console.log('✅ Pet initialized');
  },

  updateAdventureStatus: async (adventure_id: number, status: string, return_time?: string) => {
    if (!adventure_id) return;
    const response = await apiClient.post('/api/adventure', {
      adventure_id: adventure_id,
      status,
      return_time
    });

    if (response.ok) {
      const data = await response.json();
      usePetStore.setState({ currentAdventure: data.adventure });
    }
  },

  fetchAdventure: async (userid: string) => {
    try {
      set({ loadingAdventure: true });
      const todayFinishedLessonCount = await getTodayFinishedLessonCount(userid);
      // const todayFinishedLessonCount = 100;

      // 调用 API 获取 adventure，传递用户的时区偏移量（分钟）
      const timezoneOffset = new Date().getTimezoneOffset();
      const response = await apiClient.get(`/api/adventure?timezoneOffset=${timezoneOffset}`);

      if (response.ok) {
        const data = await response.json();
        // API 总是返回 adventure
        if (!data.adventure) {
          console.log('no adventure');
          set({ loadingAdventure: false });
          return;
        }

        // 如果是新的adventure，确保gain字段正确
        const adventure = {...data.adventure};
        if (!adventure.gain) {
          adventure.gain = false;
        }

        // 计算是否有可用的旅行机会
        const todayCompletedCount = data.todayCompletedCount || 0;
        const hasAvailableAdventure = todayFinishedLessonCount > todayCompletedCount;
        // setIsTaskFinished(hasAvailableAdventure);
        console.log('hasAvailableAdventure', hasAvailableAdventure);

        usePetStore.setState({ currentAdventure: adventure });

        // 如果有reward_id，获取对应的卡片信息
        if (adventure.reward_id) {
          const { data: card } = await supabase
            .from('pet_cards')
            .select('*')
            .eq('id', adventure.reward_id)
            .single();
          if (card) {
            usePetStore.setState({ adventureReward: card });
          }
        }

        // 设置状态
        if (!adventure.status || adventure.status === 'idle' || adventure.status === 'unstarted' || adventure.status === 'ready') {
          // 根据今天完成的任务数量和旅行次数决定状态
          if (hasAvailableAdventure) {
            await get().updateAdventureStatus(adventure.id, 'ready');
          } else {
            await get().updateAdventureStatus(adventure.id, 'unstarted');
          }
        } else if (adventure.status === 'in_progress' && adventure.return_time) {
          const returnTime = Number(adventure.return_time);
          const currentTime = new Date().getTime();
          if (returnTime > currentTime) {
          } else {
            // 时间已到，更新为完成状态
            await get().updateAdventureStatus(adventure.id, 'completed');
          }
        }
      }
      set({ loadingAdventure: false });
    } catch (error) {
      console.error('Error fetching adventure:', error);
      set({ loadingAdventure: false });
    }
  },

  // Countdown methods
  setRemainingSeconds: (seconds) => set({ remainingSeconds: seconds }),
  setIsCollectingReward: (isCollecting) => set({ isCollectingReward: isCollecting }),

  startCountdown: (adventureId: number) => {
    const { currentAdventure, updateAdventureStatus } = get();

    if (!currentAdventure || currentAdventure.status !== 'in_progress') {
      return;
    }

    const returnTime = Number(currentAdventure.return_time);
    const currentTime = new Date().getTime();

    if (returnTime > currentTime) {
      const initialSeconds = Math.floor((returnTime - currentTime) / 1000);
      set({ remainingSeconds: initialSeconds });

      // Clear any existing interval
      if ((usePetStore as any).countdownIntervalId) {
        clearInterval((usePetStore as any).countdownIntervalId);
      }

      // Start new countdown interval
      (usePetStore as any).countdownIntervalId = setInterval(() => {
        const state = get();
        if (state.remainingSeconds <= 1) {
          clearInterval((usePetStore as any).countdownIntervalId);
          (usePetStore as any).countdownIntervalId = null;
          set({ remainingSeconds: 0 });
          updateAdventureStatus(adventureId, 'completed');

          // 倒计时结束，如果页面在后台则启动标签闪烁
          if (typeof window !== 'undefined' && document.hidden) {
            import('@/lib/tabNotification').then(({ default: tabNotification }) => {
              tabNotification.startBlinking('🎉 Pengi is back!', { interval: 1000 });
            });
          }
        } else {
          set({ remainingSeconds: state.remainingSeconds - 1 });
        }
      }, 1000);
    } else {
      set({ remainingSeconds: 0 });
    }
  },

  stopCountdown: () => {
    if ((usePetStore as any).countdownIntervalId) {
      clearInterval((usePetStore as any).countdownIntervalId);
      (usePetStore as any).countdownIntervalId = null;
    }
    set({ remainingSeconds: 0 });
  }
}));

export default usePetStore;